// Lightweight dependency-free subsequence fuzzy matcher.
//
// Algorithm: checks whether every character in `query` appears in `text` in
// order (subsequence match). Score rewards earlier and consecutive matches so
// that "pro" ranks "Projects" above "Apollo Promo".
//
// Exported surface:
//   fuzzyMatch(text, query) → FuzzyMatch | null  (null = no match)
//   fuzzyFilter(items, query, getText?) → ranked array

export interface FuzzyMatch {
  /** Score — higher is a better match. */
  score: number;
  /** Indices into `text` that matched each character of `query`. */
  indices: number[];
}

/**
 * Returns a FuzzyMatch when every character of `query` is found in `text`
 * as a (case-insensitive) subsequence, or null when there is no match.
 *
 * Scoring heuristics (additive):
 * - +10 for each matched character
 * - +5 bonus when a match is consecutive with the previous match
 * - +3 bonus when the match is at the start of the text
 * - +2 bonus when the match is at a word boundary (preceded by space, - or _)
 */
export function fuzzyMatch(text: string, query: string): FuzzyMatch | null {
  if (!query) {
    // Empty query matches everything with zero score.
    return { score: 0, indices: [] };
  }

  const tLower = text.toLowerCase();
  const qLower = query.toLowerCase();

  let ti = 0;
  let qi = 0;
  const indices: number[] = [];

  while (ti < tLower.length && qi < qLower.length) {
    if (tLower[ti] === qLower[qi]) {
      indices.push(ti);
      qi++;
    }
    ti++;
  }

  // If we didn't match all query characters it's a no-match.
  if (qi < qLower.length) return null;

  // Compute score.
  let score = 0;
  for (let i = 0; i < indices.length; i++) {
    const idx = indices[i]!;
    score += 10;
    if (idx === 0) score += 3;
    if (i > 0 && idx === indices[i - 1]! + 1) score += 5;
    const prevChar = idx > 0 ? tLower[idx - 1] : "";
    if (prevChar === " " || prevChar === "-" || prevChar === "_") score += 2;
  }

  return { score, indices };
}

/**
 * Filters an array of items to those that fuzzy-match `query`, sorted by
 * descending score (best match first).
 *
 * @param items    The items to filter.
 * @param query    The search string.
 * @param getText  Extracts the string to match against from an item.
 *                 Defaults to `String(item)`.
 */
export function fuzzyFilter<T>(
  items: T[],
  query: string,
  getText: (item: T) => string = (item) => String(item)
): T[] {
  if (!query) return items;

  const scored: Array<{ item: T; score: number }> = [];

  for (const item of items) {
    const text = getText(item);
    const m = fuzzyMatch(text, query);
    if (m !== null) {
      scored.push({ item, score: m.score });
    }
  }

  scored.sort((a, b) => b.score - a.score);
  return scored.map((s) => s.item);
}
